import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, map, Observable, of, shareReplay, switchMap, tap} from 'rxjs';
import {inject, Injectable} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {WarningModalComponent,
  WarningModalComponentData} from '../../../shared/modals/warning-modal/warning-modal.component';
import {BenefitsService} from '../benefits/benefits.service';
import {CartChange,
  CartChangeType,
  CartSummaryTableTotalsRowViewModel,
  CartSummaryTableViewModel,
  CompleteCart,
  CompletedCartAddedByType} from './models';
import {Cart, ScreenType} from '../../models/cart';
import {EmployeeService} from '../employee/employee.service';
import {EnrolmentBlobStorageService} from '../blob-storage/enrolment-blob-storage.service';
import {Benefit, RequireNominee} from '../../models/benefit';
import {BenefitPlan} from '../../models/benefit-plan';
import {EmiConfiguration} from '../configuration/configuration';
import {Nominee} from '../../models/nominee';
import {EnrolmentBlobStorageLocation} from '../blob-storage/enrolment-blob-storage';
import {Employee} from '../../models/employee';
import {UserConfigurationService} from '../user-configuration/user-configuration.service';
import {FeatureFlagsService} from 'src/app/shared/services/feature-flag/feature-flags.service';
import {EndorsementPeriod} from '../../models/endorsement';
import {join} from '../../../shared/strings/joins';
import {DialogConfiguration} from '../../../shared/views/dialog-configuration';
import {CART_LOAD_SERVICE, CartLoadService, CartResponse} from './cart-load.service';
import {Dependent} from '../../models/dependent';
import {BenefitsDisplayable, BenefitsDisplayableType} from '../../models/benefit-displayable';
import {ENVIRONMENT} from 'src/environments/environment.token';
import {FamilyDefinition} from '../../models/family-definition';
import {WalletHistoryService} from 'src/app/px/enrolment/policy-group-overview/employee-overview/wallet-history/services/wallet-consumption/wallet-history.service';
import {IdNormalizationService} from 'src/app/shared/services/id-normalization/id-normalization.service';
const small = DialogConfiguration.small;

export enum CartLoadState {
  UNLOADED,
  LOADING,
  LOADED
}

@Injectable({
  providedIn: 'root',
})
export class CartService {
  private readonly benefitsService = inject(BenefitsService);
  private readonly configurationService = inject(UserConfigurationService);
  private readonly http = inject(HttpClient);
  private readonly employeeService = inject(EmployeeService);
  private readonly blobStorageService = inject(EnrolmentBlobStorageService);
  private readonly dialog = inject(MatDialog);
  private readonly featureFlagService = inject(FeatureFlagsService);
  private readonly cartLoadService = inject<CartLoadService>(CART_LOAD_SERVICE);
  private readonly walletHistoryService = inject(WalletHistoryService);
  private readonly idNormalizationService = inject(IdNormalizationService);
  private readonly environment = inject(ENVIRONMENT);

  public summaryPdf$ = this.configurationService.configuration$.pipe(
    shareReplay(1),
    switchMap(() => this.http.get<{downloadUrl: string}>(`${this.environment.appConfig.apiBaseUrl}/user/benefit-summary-pdf`))
  );

  private _canCompleteBenefitsSelection$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  private _isMandatoryBenefitAdded = true;
  private _isMandatoryNomineeAdded = true;
  private _isCartCompletedBySelf = true;
  private _cartActionCompleted$: BehaviorSubject<boolean> = new BehaviorSubject(true);

  private updateCartChanges(changes: CartChange[]) {
    this.cart.sections.forEach((section) => section.benefitToBenefitOptionMap.clear());
    changes.forEach((change) => {
      if (change.change === CartChangeType.VISIT_BENEFITS_PAGE) {
        this.hasVisitedBenefitsPage = true;
        this.hasVisitedDependentsPage = true;
        this.hasVisitedOverviewPage = true;
      }
      if (change.change === CartChangeType.VISIT_USER_PROFILE_PAGE) {
        this.hasVisitedUserProfilePage = true;
        this.hasVisitedOverviewPage = true;
      }
      if (change.change === CartChangeType.VISIT_DEPENDENT_PAGE) {
        this.hasVisitedDependentsPage = true;
        this.hasVisitedOverviewPage = true;
      }
      if (change.change === CartChangeType.RESET_DEFAULT) {
        this.showResetModal = true;
      }
    });
  }

  private walletAmount: number = 0;
  private fixedCostAmount: number = 0;

  private cart: Cart = Cart.make(
    this.configurationService.configuration,
    this.benefitsService.benefitSections,
    this.benefitsService.benefits,
    this.walletAmount,
    this.fixedCostAmount,
    undefined
  );

  private completeCart: Cart = Cart.make(
    this.configurationService.configuration,
    this.benefitsService.benefitSections,
    this.benefitsService.benefits,
    this.walletAmount,
    this.fixedCostAmount,
    undefined
  );

  private allCompleteCart: Cart[] = [];
  private allCart: Cart[] = [];
  private cartLoadState = new BehaviorSubject<CartLoadState>(
    CartLoadState.UNLOADED
  );

  get cartState(): Observable<CartLoadState> {
    return this.cartLoadState.asObservable();
  }

  setCartState(state: CartLoadState) {
    this.cartLoadState.next(state);
  }

  hasVisitedOverviewPage = false;
  hasVisitedBenefitsPage = false;
  hasVisitedUserProfilePage = false;
  hasVisitedDependentsPage = false;
  hasVisitedCheckoutPage = false;
  isPlanDeleted = false;
  showResetModal = false;
  showCompleteCartModal = false;
  restrictMultipleEndorsementOperation: boolean = false;
  isAutoCheckedOutWithDefaultPlan: boolean = false;

  get canCompleteBenefitsSelection$() : Observable<boolean> {
    return this._canCompleteBenefitsSelection$.asObservable();
  }

  get isMandatoryBenefitAddedToCart() {
    return this._isMandatoryBenefitAdded;
  }

  get isMandatoryNomineeAdded() {
    return this._isMandatoryNomineeAdded;
  }

  get isCartCompletedBySelf() {
    return this._isCartCompletedBySelf;
  }

  get canProceedWithCheckout() {
    return this._canCompleteBenefitsSelection$.value &&
           this.isMandatoryBenefitAddedToCart &&
           this.isMandatoryNomineeAdded;
  }

  get cartActionCompleted(): Observable<boolean> {
    return this._cartActionCompleted$.asObservable();
  }

  set cartActionCompleted(value: boolean) {
    this._cartActionCompleted$.next(value);
  }

  emiValue: EmiConfiguration | undefined;
  setEmi() {
    this.cart.selectedEmi = this.emiValue;
  }

  // To reset cart load state value, once the user navigated back fro HR Dashbaord
  resetCartLoadState() {
    if (this.cartLoadService.hasEmployeeNumber && this.cartLoadService.hasEmployeeNumber()) {
      this.cartLoadState.next(CartLoadState.UNLOADED);
      if (this.cartLoadService.reset) {
        this.cartLoadService.reset();
      }
    }
  }

  resetEntries() {
    this.hasVisitedOverviewPage = false;
    this.hasVisitedBenefitsPage = false;
    this.hasVisitedUserProfilePage = false;
    this.hasVisitedDependentsPage = false;
    this.hasVisitedCheckoutPage = false;
  }

  initialLoadCart(): Observable<any> {
    if (this.cartLoadState.value !== CartLoadState.UNLOADED) {
      return of(true);
    }
    this.updateLoadCartState(CartLoadState.LOADING);

    return this.loadCart(true).pipe(
      switchMap(() => {
        this.updateLoadCartState(CartLoadState.LOADED);

        return this.employeeService.employee$.pipe(
          switchMap((employee) => this.walletHistoryService.getWalletHistory(
            employee.policyGroupId,
            employee.id
          ))
        );
      })
    );
  }

  private handleCartResponse = () => {
    return tap((response: CartResponse) => {
      if (!response) {
        return;
      }
      const cartChanges = response.cart;

      // Depending on the feature flag `pp-new-complete-cart`, the complete cart can be an object or an array of objects.
      // If there are multiple complete carts, filter out the active complete cart.
      // If no active complete cart is found, process both complete carts based on endorsement id.
      let completeCart: CompleteCart[];

      if (Array.isArray(response.completeCart)) {
        completeCart = [...response.completeCart];
      } else {
        completeCart = [response.completeCart];
      }

      const activeEnrolment = this.employeeService.activeEnrolment;

      if (activeEnrolment) {
        const activeCompleteCart = completeCart.find(
          (cart) => cart.endorsementId === activeEnrolment?.id
        );

        completeCart.length = 0;
        completeCart = [activeCompleteCart!];
      }

      this.cart = Cart.make(
        this.configurationService.configuration,
        this.benefitsService.benefitSections,
        this.benefitsService.benefits,
        this.walletAmount,
        this.fixedCostAmount,
        completeCart[0]
      );

      this.completeCart = Cart.make(
        this.configurationService.configuration,
        this.benefitsService.benefitSections,
        this.benefitsService.benefits,
        this.walletAmount,
        this.fixedCostAmount,
        completeCart[0]
      );

      // Loop through complete cart api response, look out for multiple cart and complete cart
      this.allCart.length = 0;
      this.allCompleteCart.length = 0;

      // completeCart.length can be greater than 1 if there are multiple complete carts due to:
      // 1. Multiple endorsement periods
      // 2. Presence of a mid-term cart
      if (completeCart.length > 1) {
        completeCart.forEach((cartCompleted: CompleteCart) => {
          const cart = Cart.make(
            this.configurationService.configuration,
            this.benefitsService.benefitSections,
            this.benefitsService.benefits,
            this.walletAmount,
            this.fixedCostAmount,
            cartCompleted
          );

          this.allCart.push(cart);

          // New complete carts
          const completedCart = Cart.make(
            this.configurationService.configuration,
            this.benefitsService.benefitSections,
            this.benefitsService.benefits,
            this.walletAmount,
            this.fixedCostAmount,
            cartCompleted
          );

          this.allCompleteCart.push(completedCart);
        });
        if (this.midtermCompletedCart && this.midtermCompletedCart.length > 0) {
          this.midtermCompletedCart[0].wallet =
            this.annualCompletedCart[0].wallet -
            this.annualCompletedCart[0].consumedWallet!;
        }

        this.cart = this.allCart[0];
        this.completeCart = this.allCompleteCart[0];
      } else {
        this.allCart.push(this.cart);
        this.allCompleteCart.push(this.completeCart);
      }
      this.resetEntries();
      this.updateCartChanges(cartChanges);
      this.applyChanges(
        this.benefitsService.benefits,
        cartChanges,
        activeEnrolment
      );

      // Perform the below check oly on benefit which is applicable to user
      let userDisplayedBenefits = this.benefitsService.benefitsApplicableToUser(
        this.employeeService.currentEmployee,
        this.configurationService.configuration
      );

      this._canCompleteBenefitsSelection$.next(
        this.cart.nonEmptySections.length !== 0 && this.isDefaultPlansAdded(userDisplayedBenefits)
      );

      this.restrictMultipleEndorsementOperation =
        this.restrictMultipleEndorsementOperationValue(response.completeCart);

      this.isAutoCheckedOutWithDefaultPlan = this.checkAutoCheckedOutWithDefaultPlan(response.completeCart);

      const completeCarts = Array.isArray(response.completeCart) ? response.completeCart : [response.completeCart];

      this._isCartCompletedBySelf = completeCarts[completeCarts.length - 1]?.addedBy === CompletedCartAddedByType.SELF;

      /**
       * Filters the `userDisplayedBenefits` array to include only those benefits that meet specific criteria:
       *
       * 1. If not all active plans of a benefit are linked to another plan (`linkedPlanId` is missing), the benefit is included.
       * 2. If all active plans are linked:
       *    - The benefit is included only if at least one of the linked plans is present in the cart.
       *    - Additionally, the benefit must be marked as mandatory (`benefit.isMandatory`).
       *
       * This logic ensures that benefits are shown based on plan linkage and cart selection status.
       *
       * @returns {Benefit[]} A filtered array of benefits applicable to the user based on plan linkage and cart contents.
       */

      userDisplayedBenefits = userDisplayedBenefits.filter((benefit) => {
        const allPlans = benefit.allPlans.filter((plan) => plan.isActive);
        const allLinked = allPlans.every((plan) => plan.linkedPlanId.length > 0);

        if (!allLinked) {
          return true;
        }

        const selectedPlanIds = this.selectedPlans();
        const anyLinkedPlanInCart = allPlans.some(
          (plan) => plan.linkedPlanId.some((id) => selectedPlanIds.includes(id))
        );

        return anyLinkedPlanInCart && benefit.isMandatory;
      });

      userDisplayedBenefits = userDisplayedBenefits.filter((benefit) => {
        let matchedFamilyDefinition: FamilyDefinition | undefined = undefined;

        if (benefit.linkedBenefitId) {
          const linkedBenefit = userDisplayedBenefits.find(
            (d) => d.id === benefit.linkedBenefitId
          );

          if (!linkedBenefit) {
            return;
          }

          this.getCart()
            .addedPlansForBenefit(linkedBenefit)
            .forEach((plan: BenefitPlan) => {
              if (!plan.familyDefinition) {
                return;
              }
              matchedFamilyDefinition = plan.familyDefinition;
            });
          const planList = benefit.plansForEmployee(
            this.employeeService.currentEmployee,
            this.configurationService.configuration,
            matchedFamilyDefinition,
            this.selectedPlans(),
            benefit.requireLinkedBenefitFamilyDefinition,
            benefit.requireLinkedBenefitDifferentDependents,
            true
          );

          return planList.length > 0;
        }

        return true;
      });

      this.checkMandatoryBenefit(userDisplayedBenefits);
      this.isMandatoryNomineeForBenefitAdded(userDisplayedBenefits);
      this.updateMidtermPlans();

      this.latestCart.next(this.cart);
      this.updateLoadCartState();
      this.setEmi();
      this.walletHistoryService.refreshWalletHistory();
    });
  };

  loadCart(useSpinner: boolean = false): Observable<any> {
    return this.cartLoadService.loadCart(useSpinner).pipe(
      this.handleCartResponse()
    );
  }

  restrictMultipleEndorsementOperationValue(completeCart: CompleteCart | CompleteCart[]) {
    const arrayCartIsCompleted = Array.isArray(completeCart) && completeCart.length > 0 &&
      completeCart.some((cart) => cart.addedBy === CompletedCartAddedByType.SELF);
    const singleCompletedCartCompleted = !Array.isArray(completeCart) && completeCart &&
      completeCart.addedBy === CompletedCartAddedByType.SELF;
    const isEnrolmentCheckoutCompletedBySelf = completeCart &&
          (arrayCartIsCompleted || singleCompletedCartCompleted);

    return (
      !this.configurationService.configuration.portal.allowMultipleEnrolments &&
      isEnrolmentCheckoutCompletedBySelf
    );
  }

  checkAutoCheckedOutWithDefaultPlan(cart: CompleteCart | CompleteCart[]): boolean {
    if (Array.isArray(cart)) {
      return cart.some((item) => !!item?.userId && item?.addedBy === undefined);
    }

    return !!cart?.userId && cart?.addedBy === undefined;
  }

  updateLoadCartState(state? : CartLoadState) {
    this.cartLoadState.next(state ? state : CartLoadState.LOADED);
  }

  // To check if all Mandatory Benefits plan is added to cart
  private isDefaultPlansAdded(userBenefit: Benefit[]) {
    const defaultBenefitPlans = this.benefitsService.mandatoryBenefits(
      this.employeeService.currentEmployee,
      this.selectedPlans()
    );

    return this.cart.isDefaultPlanAddedToCart(defaultBenefitPlans, userBenefit);
  }

  private updateMidtermPlans() {
    if (!this.midtermCompletedCart || !this.isMidtermEnrolment()) {
      return;
    }

    // For all non empty sections of midterm complete cart
    // loop through each plan and check if some plan exists in annual enrolment
    // if exists, set annualEnrolmentPlan for that plan
    const midtermNonEmptySections = this.midtermCompletedCart[0].nonEmptySections;

    midtermNonEmptySections.forEach((midtermSection) => {
      midtermSection.benefitToBenefitOptionMap.forEach(
        (midtermPlan, benefitId) => {
          // Now, for multiselect beenfits, we need to set annual enrolment plan for each plan of midterm plan array
          midtermPlan.forEach((plan) => {
            const annualPlan = this.planForSameBenefitDuringAnnualEnrolment(
              benefitId,
              plan.id
            );

            if (annualPlan) {
              const annualPlanDependents =
                this.annualCompletedCart[0].dependentsForPlan(annualPlan, this.employeeService.currentEmployee);

              this.benefitsService.setAnnualEnrolmentPlanForMidtermPlan(
                plan.id,
                benefitId,
                annualPlan,
                annualPlanDependents
              );
            }
          });
        }
      );
    });
  }

  private planForSameBenefitDuringAnnualEnrolment(
    benefitId: number,
    planId: number
  ) {
    const annualNonEmptySections = this.annualCompletedCart[0].nonEmptySections;

    // fetch from annualNonEmptySections using benefit id
    const benefitSection = annualNonEmptySections.find((section) => section.benefitToBenefitOptionMap.has(benefitId));
    let benefitPlan = benefitSection?.benefitToBenefitOptionMap.get(benefitId);

    if (!benefitPlan) {
      return undefined;
    }

    // if length of benefitPlan is 1, return that plan
    // this is for single select benefits
    if (benefitPlan.length === 1) {
      return benefitPlan[0];
    }

    // if length of benefitPlan is more than 1, return the plan with same plan id
    // this is for multi select benefits
    benefitPlan = benefitPlan.filter((plan) => plan.id === planId);

    return benefitPlan[0];
  }

  applyChanges(
    benefits: Benefit[],
    cartChanges: CartChange[],
    activeEnrolment: EndorsementPeriod | undefined
  ) {
    // Apply changes to cart based on enrolment id(Multiple Enrolment)
    if (this.featureFlagService.newCompleteCart) {
      // Apply changes cart of enrollment period whilch is active
      if (activeEnrolment) {
        cartChanges.forEach((change) => {
          if (change.endorsementId === activeEnrolment?.id) {
            this.cart.applyChange(change, benefits);

            if (this.completeCart.canApplyChangeIfCompleted(change)) {
              this.completeCart.applyChange(change, benefits);
            }
          } else {
            // To grab dependents of previous enrolment period
            this.cart.previousEndorsementDependents(change);
          }
        });
      } else {
        // Apply change to cart of all enrolment period if none of endorsement window is open
        this.allCompleteCart.forEach((completedCart) => {
          cartChanges.forEach((change) => {
            if (change.endorsementId === completedCart.endorsementId) {
              const cart = this.allCart.find(
                (cart) => cart.endorsementId === completedCart.endorsementId
              );

              cart?.applyChange(change, benefits);

              if (completedCart.canApplyChangeIfCompleted(change)) {
                completedCart.applyChange(change, benefits);
              }
            }
          });
        });
      }
    } else {
    // Apply changes to cart based on enrolment id(Mid Term)
      if (this.isMidtermEnrolment()) {
        this.allCompleteCart.forEach((completedCart) => {
          cartChanges.forEach((change) => {
            if (change.endorsementId === completedCart.endorsementId) {
              const cart = this.allCart.find(
                (cart) => cart.endorsementId === completedCart.endorsementId
              );

              cart?.applyChange(change, benefits);

              if (completedCart.canApplyChangeIfCompleted(change)) {
                completedCart.applyChange(change, benefits);
              }
            }
          });
        });
      } else {
        cartChanges.forEach((change) => {
          this.cart.applyChange(change, benefits);

          if (this.completeCart.canApplyChangeIfCompleted(change)) {
            this.completeCart.applyChange(change, benefits);
          }
        });
      }
    }
  }

  isMidtermEnrolment() {
    const isMidtermEnrolmentPresent = this.allCompleteCart.some(
      (cart) => cart.midTerm
    );

    return isMidtermEnrolmentPresent && this.allCompleteCart.length > 1;
  }

  private checkMandatoryBenefit(
    benefits: Benefit[]
  ) {
    const mandatoryBenefits = benefits
      .filter((benefit) => benefit.isMandatory);

    if (!mandatoryBenefits.length) {
      this._isMandatoryBenefitAdded = true;

      return;
    }
    this._isMandatoryBenefitAdded = mandatoryBenefits
      .every((benefit) => this.getCart().benefitIsAddedToCart(benefit));

    return;
  }

  private isMandatoryNomineeForBenefitAdded(benefits: Benefit[]) {
    const nomineeMandatoryBenefits = benefits
      .filter((benefit: Benefit) => this.getCart().benefitIsAddedToCart(benefit))
      .filter(
        (benefit) => benefit.requireNomineeConfiguration === RequireNominee.MANDATORY
      );

    const benefitMandatoryMap: Set<boolean> = new Set<boolean>();

    nomineeMandatoryBenefits.forEach((benefit) => {
      let allMandatoryAdded: boolean = false;

      this.getCart()
        .addedPlansForBenefit(benefit)
        .forEach((plan: BenefitPlan) => {
          allMandatoryAdded =
            benefit.requireNomineeConfigurationForm !== RequireNominee.MANDATORY ?
              this.getCart().nomineeForBenefitPlan(plan).length > 0 :
              this.getCart().nomineeForBenefitPlan(plan).length > 0 &&
                !!this.getCart().nomineeUrlForBenefitPlan(plan.id);

          benefitMandatoryMap.add(allMandatoryAdded);
        });
    });

    return (this._isMandatoryNomineeAdded = Array.from(
      benefitMandatoryMap
    ).every((x) => x));
  }

  // This method retrieves a list of all benefits that are either mandatory or require a mandatory nominee to be added before checkout.
  fetchMandatoryDetails() {
    const benefits = this.benefitsService.benefitsApplicableToUser(
      this.employeeService.currentEmployee,
      this.configurationService.configuration
    );

    // Identify all mandatory benefits that have not been added to the cart
    const mandatoryBenefits = benefits.filter((benefit) => benefit.isMandatory);
    const mandatoryBenefitNotInCart = mandatoryBenefits.filter(
      (benefit) => !this.getCart().benefitIsAddedToCart(benefit)
    );

    // Identify all mandatory benefits that have been added to the cart and require a nominee before proceeding to checkout
    const nomineeMandatoryBenefits = benefits.filter((benefit) => this.getCart().benefitIsAddedToCart(benefit) &&
      benefit.requireNomineeConfiguration === RequireNominee.MANDATORY);

    const mandatoryNomineeNotInCart = nomineeMandatoryBenefits.filter(
      (benefit) => !this.getCart().addedPlansForBenefit(benefit)
        .every((plan) => this.getCart().nomineeForBenefitPlan(plan).length > 0 &&
        (benefit.requireNomineeConfigurationForm !== RequireNominee.MANDATORY ||
           !!this.getCart().nomineeUrlForBenefitPlan(plan.id)))
    );

    return {
      mandatoryBenefitNotInCart,
      mandatoryNomineeNotInCart,
    };
  }

  getCart(): Cart {
    return this.cart;
  }

  get completedCart(): Cart {
    if (this.isMidtermEnrolment()) {
      return this.midtermCompletedCart[0];
    }

    return this.completeCart;
  }

  get allCompletedCarts(): Cart[] {
    // If allCompleteCart has more than one item, it indicates either multiple enrolments or mid-term enrolments.
    return this.allCompleteCart.length > 1 ? this.sortedCompletedCarts : this.allCompleteCart;
  }

  getActiveBenefitsInCart(benefits: Benefit[]): Benefit[] {
    const cart = this.getCart();

    return benefits.filter((benefit) => benefit.isActive &&
      cart.benefitIsAddedToCart(benefit) &&
      !this.benefitsService.isExpiredBenefit(benefit));
  }

  // Retrieve all completed carts sorted by the start date of the endorsement period
  get sortedCompletedCarts(): Cart[] {
    return this.employeeService.sortedEndorsemtPeriod
      .map((period) => this.allCompleteCart.find((completedCart) => completedCart.endorsementId === period.id))
      .filter((cart): cart is Cart => cart !== undefined);
  }

  get midtermCompletedCart(): Cart[] {
    return this.allCompleteCart.filter((cart) => cart.midTerm);
  }

  get annualCompletedCart(): Cart[] {
    return this.allCompleteCart.filter((cart) => !cart.midTerm);
  }

  setCostAmounts(walletAmount: number, fixedCostAmount: number) {
    this.walletAmount = walletAmount;
    this.fixedCostAmount = fixedCostAmount;
  }

  private latestCart: BehaviorSubject<Cart> = new BehaviorSubject<Cart>(
    this.cart
  );

  liveCart(): Observable<Cart> {
    return this.latestCart.asObservable();
  }

  addBenefit(
    benefit: Benefit,
    plan: BenefitPlan,
    allDependents: Set<number>
  ) {
    const cartChanges: CartChange[] = [];
    const samePlanIsAddedToCart = this.cart.benefitPlanIsAddedToCart(
      benefit,
      plan
    );

    cartChanges.push({
      benefitId: benefit.id,
      planId: plan.id,
      change: CartChangeType.ADDITION,
      dependentsIds: Array.from(allDependents),
    });

    const addedBenefits = this.cart.benefitMapForBenefit(benefit);

    if (
      (!benefit.multiSelect && addedBenefits.has(benefit.id)) ||
      samePlanIsAddedToCart
    ) {
      return this.removeBenefit(
        benefit,
        addedBenefits.get(benefit.id) || [],
        true
      ).pipe(
        switchMap((removed) => {
          if (removed) {
            return this.http.post<CartResponse>(`${this.environment.appConfig.apiBaseUrl}/user/cart`, cartChanges).pipe(
              this.handleCartResponse()
            );
          } else {
            return of(false);
          }
        })
      );
    } else {
      return this.http.post<CartResponse>(`${this.environment.appConfig.apiBaseUrl}/user/cart`, cartChanges).pipe(
        this.handleCartResponse()
      );
    }
  }

  addNominee(
    benefit: Benefit,
    plan: BenefitPlan,
    endorsementId: number,
    nominees?: Nominee[],
    fileUrl?: string
  ): Observable<any> {
    const cartChanges: CartChange[] = [];

    if (nominees !== undefined && nominees.length > 0) {
      cartChanges.push({
        benefitId: benefit.id,
        planId: plan.id,
        change: CartChangeType.ADD_NOMINEES,
        nominees: nominees,
        endorsementId: endorsementId,
      });
    }

    if (fileUrl !== undefined) {
      cartChanges.push({
        benefitId: benefit.id,
        planId: plan.id,
        change: CartChangeType.UPLOAD_NOMINEE_FORM,
        uploadNomineesForm: fileUrl,
        endorsementId: endorsementId,
      });
    }

    /*
    * When there are no nominee changes to save, treat the operation as successful.
    * Returning false causes cartWritesSucceeded() to fail even though there is
    * nothing to persist, which incorrectly blocks downstream flows such as
    * Add & Next navigation.
    */
    if (cartChanges.length === 0) {
      return of(true);
    }

    return this.http.post<CartResponse>(`${this.environment.appConfig.apiBaseUrl}/user/cart`, cartChanges)
      .pipe(
        this.handleCartResponse(),
        map(() => true)
      );
  }

  removeBenefit(
    benefit: Benefit,
    plans: BenefitPlan[],
    implicitRemove: boolean
  ): Observable<boolean> {
    const remove = (changes: CartChange[]) => {
      this.isPlanDeleted = true;

      return this.http.post<CartResponse>(`${this.environment.appConfig.apiBaseUrl}/user/cart`, changes).pipe(
        this.handleCartResponse(),

        // A failed write reaches us as an empty response, because the error interceptor
        // turns the failure into a success carrying no body. Reporting `true` regardless
        // told callers the plan had been removed when it had not.
        map((response) => !!response)
      );
    };

    const cartChanges: CartChange[] = plans.map((plan) => {
      return {
        benefitId: benefit.id,
        planId: plan.id,
        change: CartChangeType.REMOVAL,
      };
    });

    const linkedBenefitsNames: string[] = [];

    let linkedBenefits = this.benefitsService.benefits.filter(
      (b) => b.linkedBenefitId === benefit.id &&
        this.getCart().benefitIsAddedToCart(b) &&
        !(this.disableForAirtelMarketplace(b) ||
        this.disableForEYBenefit(b))
    );

    // Reset the value canCompleteBenefitSelection
    this._canCompleteBenefitsSelection$.next(false);

    // Use BFS to remove all linked benefits
    while (linkedBenefits.length > 0) {
      linkedBenefitsNames.push(...linkedBenefits.map((b) => b.title));
      const nextLinkedBenefits: Benefit[] = [];

      linkedBenefits.forEach((linkedBenefit) => {
        this.cart.addedPlansForBenefit(linkedBenefit).map((linkedPlan) => {
          cartChanges.push({
            benefitId: linkedBenefit.id,
            planId: linkedPlan.id,
            change: CartChangeType.REMOVAL,
          });

          const linkedBenefitsWithAddedPlans = this.benefitsService.benefits.filter((b) => {
            return (
              b.linkedBenefitId === linkedBenefit.id &&
              this.getCart().benefitIsAddedToCart(b) &&
              !(this.disableForAirtelMarketplace(b) ||
              this.disableForEYBenefit(b))
            );
          });

          nextLinkedBenefits.push(...linkedBenefitsWithAddedPlans);
        });
      });

      linkedBenefits = nextLinkedBenefits;
    }

    if (cartChanges.length > 1) {
      let title = `Are you sure you want to remove ${join(plans.map((p) => p.name))}?`;

      if (implicitRemove) {
        title = `Switching your plan from ${join(plans.map((p) => p.name))} will remove other choices you've made. Are you sure you want to proceed?`;
      }

      const data: WarningModalComponentData = {
        title: title,
        subtitle: `Plans from <b>${join(linkedBenefitsNames)}</b> will also be removed from your selected plans list.`,
        continue: 'Ok',
        cancel: 'Cancel',
        imageSrc: 'assets/glyphs/modal/warning.svg',
        loggingTitle: 'remove-linked-plans',
        loggingExtras: {
          benefitId: benefit.id,
          linkedBenefits: linkedBenefits.map((b) => b.id),
        },
      };

      return this.dialog
        .open(WarningModalComponent, small<WarningModalComponentData>(data))
        .afterClosed()
        .pipe(
          switchMap((result) => {
            if (result) {
              return remove(cartChanges).pipe(map(() => true));
            } else {
              return of(false);
            }
          })
        );
    } else {
      return remove(cartChanges);
    }
  }

  markUserProfilePageVisited(): Observable<any> {
    if (this.hasVisitedUserProfilePage) {
      return of(false);
    }
    const cartChanges: CartChange[] = [
      {
        benefitId: -1,
        planId: -1,
        change: CartChangeType.VISIT_USER_PROFILE_PAGE,
      },
    ];

    return this.http.post<CartResponse>(`${this.environment.appConfig.apiBaseUrl}/user/cart`, cartChanges).pipe(
      this.handleCartResponse()
    );
  }

  markDependentsPageVisited(): Observable<any> {
    if (this.hasVisitedDependentsPage) {
      return of(false);
    }
    const cartChanges: CartChange[] = [
      {
        benefitId: -1,
        planId: -1,
        change: CartChangeType.VISIT_DEPENDENT_PAGE,
      },
    ];

    return this.http.post<CartResponse>(`${this.environment.appConfig.apiBaseUrl}/user/cart`, cartChanges).pipe(
      this.handleCartResponse()
    );
  }

  markBenefitsSelectionPageVisited(): Observable<any> {
    if (this.hasVisitedBenefitsPage && this.getCart().hasCompletedCart) {
      return of(false);
    }
    const cartChanges: CartChange[] = [
      {
        benefitId: -1,
        planId: -1,
        change: CartChangeType.VISIT_BENEFITS_PAGE,
      },
    ];

    return this.http.post<CartResponse>(`${this.environment.appConfig.apiBaseUrl}/user/cart`, cartChanges).pipe(
      this.handleCartResponse()
    );
  }

  uploadNomineeData(
    policyGroupId: number | string,
    nomineeDataFile: File
  ): Observable<string> {
    return this.blobStorageService.uploadBlob(
      policyGroupId,
      nomineeDataFile,
      nomineeDataFile.name.replace(' ', '_'),
      EnrolmentBlobStorageLocation.NOMINEES,
      true,
      undefined,
      true
    );
  }

  completeBenefitSelection(emi?: EmiConfiguration) {
    this.emiValue = emi;

    return this.http
      .post<CompleteCart>(`${this.environment.appConfig.apiBaseUrl}/user/complete-cart`, {
        userId: this.employeeService.currentEmployee.id,
        emiId: emi?.id,
        midTerm: Employee.isMidTermActive(this.employeeService.currentEmployee),
      })
      .pipe(
        switchMap(() => {
          this.showCompleteCartModal = true;

          this.summaryPdf$ = this.http.get<{downloadUrl: string}>(`${this.environment.appConfig.apiBaseUrl}/user/benefit-summary-pdf`)
            .pipe(shareReplay());

          return this.loadCart();
        })
      );
  }

  selectedPlans(): number[] {
    return CartService.addedPlansIds(this.cart);
  }

  private completedPlans(): number[] {
    return CartService.addedPlansIds(this.completedCart);
  }

  get hasUnsavedChanges() {
    return (
      JSON.stringify(this.selectedPlans().sort()) !==
      JSON.stringify(this.completedPlans().sort())
    );
  }

  get allCoveredDependents() {
    const coveredDependents: Dependent[] = [];

    this.cart.sections.forEach((section) => {
      section.benefitToBenefitOptionMap.forEach((plans) => {
        plans.forEach((plan) => coveredDependents.push(
          ...this.cart.dependentsForPlan(plan, this.employeeService.currentEmployee)
        ));
      });
    });

    return coveredDependents;
  }

  private static addedPlansIds(cart: Cart): number[] {
    const addedPlans: number[] = [];

    cart.sections.forEach((section) => {
      section.benefitToBenefitOptionMap.forEach((plans) => {
        addedPlans.push(...plans.map((plan) => plan.id));
      });
    });

    return addedPlans;
  }

  cartViewModel(cart: Cart, showEmi: boolean, screenType:
  ScreenType = ScreenType.BENEFITS_SELECTION): CartSummaryTableViewModel {
    const viewModels = this.displayablesForCartSection(cart, null)
      .filter((displayable: BenefitsDisplayable) => {
        switch (displayable.type) {
            case BenefitsDisplayableType.SECTION:
              return this.displayablesForCartSection(cart, displayable.id).some((displayable) => {
                return displayable.isBenefit &&
                    cart.benefitIsAddedToCart(displayable.benefit!);
              });
            case BenefitsDisplayableType.BENEFIT:
              return cart.benefitIsAddedToCart(displayable.benefit!);
        }
      })
      .map((displayable: BenefitsDisplayable) => {
        const displableDetail = cart.cartRowsViewModal(
          displayable,
          this.employeeService.currentEmployee,
          displayable.type === BenefitsDisplayableType.SECTION ?
            this.displayablesForCartSection(cart, displayable.id).filter(
              (d) => d.isBenefit && cart.benefitIsAddedToCart(d.benefit!)
            ) :
            []
        );

        if (displayable.type === BenefitsDisplayableType.SECTION) {
          displableDetail.subViewModels.map((subViewModel) => {
            const benefit = this.benefitsService.benefits.find((benefit) => benefit.id === subViewModel.benefitId)!;

            subViewModel.dontAllowChangesAfterCheckout =
              this.disableForAirtelMarketplace(benefit) ||
            this.disableForEYBenefit(benefit);

            return subViewModel;
          });
        }

        if (displayable.benefit && displayable.benefit.dontAllowChangesAfterCheckout) {
          displableDetail.dontAllowChangesAfterCheckout =
            this.disableForAirtelMarketplace(displayable.benefit) ||
          this.disableForEYBenefit(displayable.benefit);
        }

        return displableDetail;
      });

    const availableWalletAmount = this.availableWallet(cart);
    const fixedCostExcessToWallet = cart.fixedCostExcessContributionAmount(
      this.employeeService.currentEmployee
    );

    const totalViewModels: CartSummaryTableTotalsRowViewModel[] = cart.cartTotalViewModels(
      this.configurationService.configuration,
      this.employeeService.currentEmployee,
      showEmi,
      availableWalletAmount,
      screenType,
      this.featureFlagService.marketPlaceLocked &&
      (this.configurationService.configuration.useWalletBeyondEnrolments ?? false),
      this.featureFlagService.ppShowBalanceWallet &&
      this.configurationService.configuration.showWalletGlobally!,
      fixedCostExcessToWallet
    );

    return {
      hideNomineeColumn: viewModels.every((viewModel) => {
        if (viewModel.subViewModels.length === 0) {
          return viewModel.nomineeButtonText === undefined;
        } else {
          return viewModel.nomineeButtonText === undefined && viewModel.subViewModels.every((subViewModel) => {
            return subViewModel.nomineeButtonText === undefined;
          });
        }
      }),
      hideSumInsured: viewModels.every((viewModel) => {
        if (viewModel.subViewModels.length === 0) {
          return viewModel.sumInsured && viewModel.sumInsured.length === 0;
        } else {
          return viewModel.sumInsured && viewModel.sumInsured.length === 0 &&
            viewModel.subViewModels.every((subViewModel) => {
              return subViewModel.sumInsured.length === 0;
            });
        }
      }),
      hideFamilyDefinition: viewModels.every((viewModel) => {
        if (viewModel.subViewModels.length === 0) {
          return viewModel.whosCovered && viewModel.whosCovered.length === 0;
        } else {
          return viewModel.whosCovered &&
            viewModel.whosCovered.length === 0 &&
            viewModel.subViewModels.every((subViewModel) => {
              return subViewModel.whosCovered.length === 0;
            });
        }
      }),
      rows: viewModels,
      totals: totalViewModels,
    };
  }

  private displayablesForCartSection(cart: Cart, parentSectionId: number | null): BenefitsDisplayable[] {
    const baseDisplayables = this.benefitsService.displayablesForSection(
      parentSectionId,
      this.employeeService.currentEmployee,
      this.configurationService.configuration
    );
    const existingBenefitIds = new Set(
      baseDisplayables
        .filter((displayable) => displayable.isBenefit)
        .map((displayable) => displayable.benefit!.id)
    );
    const selectedBenefitsInCart = this.benefitsService.benefits
      .filter((benefit) => benefit.parentSectionId === parentSectionId)
      .filter((benefit) => cart.benefitIsAddedToCart(benefit))
      .filter((benefit) => !existingBenefitIds.has(benefit.id))
      .map((benefit) => BenefitsDisplayable.benefit(benefit));

    return [
      ...baseDisplayables,
      ...selectedBenefitsInCart,
    ];
  }

  availableWallet(cart : Cart) {
    return cart.availableWalletAmount(
      this.employeeService.currentEmployee,
      this.walletHistoryService.walletHistory
    );
  }

  // This is used to calculte wallet consuption for old ux
  walletToBeConsumed(cart : Cart) {
    const availableWalletAmount = this.availableWallet(cart);

    return cart.walletToBeConsumed(
      this.employeeService.currentEmployee,
      false,
      availableWalletAmount.walletAvailableToConsume
    ).displayValue(this.employeeService.currentEmployee);
  }

  generateNomineeForm(
    benefitName: string,
    nominees: Nominee[]
  ) {
    return this.http.post<string>(`${this.environment.appConfig.apiBaseUrl}/user/generate-nominee-form`, {
      nominees: nominees,
      benefitName: benefitName,
    });
  }

  /**
   * Check if the benefit is disabled for Airtel marketplace.
   * @param benefit
   * @returns True/False
   * - complete cart is done
   * - complete cart is done by user
   * - customer is Airtel
   * - benefit is checked for new setting
   * - at least 1 plan is added for the benefit
   */
  disableForAirtelMarketplace(benefit: Benefit): boolean {
    if (!benefit) {
      return false;
    }

    const isDisabled = this.getCart().hasCompletedCart &&
      this.isCartCompletedBySelf &&
      (
        this.employeeService.currentEmployee.customerId !== undefined &&
        this.idNormalizationService.equalsRuntimeIdAndEnvId(
          this.employeeService.currentEmployee?.customerId,
          this.environment.sharedOverrides.airtelCustomerId
        )
      ) &&
      benefit.dontAllowChangesAfterCheckout &&
      this.completedCart.addedPlansForBenefit(benefit).length >= 1;

    return isDisabled;
  }

  /**
   * Check if the benefit is disabled for EY.
   * @param benefit
   * @returns True/False
   * - no check on complete cart,
   * - plan is not added by user
   * - plan is not added by admin
   * - customer is EY
   * - benefit is checked for new setting
   * - at least 1 plan is added for the benefit
   */
  disableForEYBenefit(benefit: Benefit): boolean {
    if (!benefit) {
      return false;
    }

    const isDisabled =
      !this.getCart().isAdminAddedPlan(benefit.id) &&
    !this.getCart().isUserAddedPlan(benefit.id) &&
      (
        this.employeeService.currentEmployee.customerId !== undefined &&
        this.idNormalizationService.equalsRuntimeIdAndEnvId(
          this.employeeService.currentEmployee?.customerId,
          this.environment.sharedOverrides.eyCustomerId
        )
      ) &&
      benefit.dontAllowChangesAfterCheckout &&
      this.getCart().addedPlansForBenefit(benefit).length >= 1;

    return isDisabled;
  }
}
